import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, Sparkles, Clock, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public answer view — deep-linked from the WhatsApp template message a
 * paying ₹99 customer receives. Meta's 24-hour rule blocks our multi-part
 * free-form WA messages, so instead the template carries a link here where
 * the full Parashari answer is rendered on the web.
 *
 * URL: /poocho-guruji/answer/:sessionId
 */
export default function PoochoAnswerViewPage() {
  const { sessionId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/poocho-guruji/answer-by-session/${sessionId}`);
        if (alive) setState({ loading: false, data: res.data, error: null });
      } catch (e) {
        if (alive) setState({
          loading: false, data: null,
          error: e?.response?.status === 409
            ? 'आपका उत्तर अभी तैयार हो रहा है — 1-2 मिनट में पुनः देखें।'
            : e?.response?.status === 404
              ? 'यह session नहीं मिली — link दोबारा check करें।'
              : 'कुछ गड़बड़ हुई — कुछ देर बाद कोशिश करें।',
        });
      }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#0b0410] text-white flex items-center justify-center px-4">
        <div className="text-center" data-testid="ans-view-loading">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto mb-3" />
          <p className="text-sm text-white/70">आपका ज्योतिष उत्तर load हो रहा है…</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-[#0b0410] text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center border border-white/10 rounded-2xl bg-white/[0.03] p-8" data-testid="ans-view-error">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <p className="text-sm">{state.error}</p>
          <a href="/poocho-guruji" className="inline-block mt-6 text-xs text-amber-400 underline">
            ← Poocho Guruji पर वापिस जाएँ
          </a>
        </div>
      </div>
    );
  }

  const { question, answer, answered_at } = state.data;
  const meta = answer?.meta || {};
  const headline = answer?.headline || 'आपका ज्योतिष उत्तर';
  const intro = answer?.intro || '';
  const analysis = answer?.shastriya_analysis || '';
  const vyavharik = answer?.vyavharik_salah || '';
  const closing = answer?.final_disclaimer || answer?.closing || '';
  const decision = answer?.decision || {};
  const upay = Array.isArray(answer?.upay) ? answer.upay : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b0410] via-[#0f0620] to-[#0b0410] text-white pt-6 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <div className="text-sm tracking-widest uppercase text-white/60">
            Poocho Guruji · Viratastro
          </div>
        </div>

        {/* Question card */}
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 mb-6" data-testid="ans-view-question">
          <div className="text-xs uppercase tracking-wider text-white/40 mb-1.5">आपका प्रश्न</div>
          <div className="text-[15px] leading-relaxed">{question}</div>
          {answered_at && (
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-white/40">
              <Clock className="w-3 h-3" /> उत्तर दिया गया: {new Date(answered_at).toLocaleString('hi-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
          )}
        </div>

        {/* Headline */}
        <h1
          className="font-bold text-transparent bg-clip-text mb-4 leading-tight"
          style={{
            fontFamily: '"Noto Serif Devanagari", "Playfair Display", serif',
            backgroundImage: 'linear-gradient(180deg,#FFF3B0 0%,#F4D477 40%,#B8942D 100%)',
            fontSize: 'clamp(22px, 4vw, 34px)',
          }}
          data-testid="ans-view-headline"
        >
          {headline}
        </h1>

        {/* Decision panel — verdict + favorable window only. No raw
            probability percentage per user request (arbitrary 42%/55% type
            numbers should never surface to a paying customer). */}
        {(decision.verdict_hi || decision.favorable_period_hi) && (
          <div className="mb-6 rounded-xl bg-emerald-500/[0.07] border border-emerald-500/25 p-4 flex flex-wrap gap-4 text-sm" data-testid="ans-view-decision">
            {decision.verdict_hi && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-300/70">निर्णय</div>
                <div className="font-semibold">{decision.verdict_hi}</div>
              </div>
            )}
            {decision.favorable_period_hi && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-300/70">Favorable Period</div>
                <div className="font-semibold">{decision.favorable_period_hi}</div>
              </div>
            )}
          </div>
        )}

        {/* Sections */}
        {intro && (
          <Section title="प्रस्तावना" body={intro} />
        )}
        {analysis && (
          <Section title="शास्त्रीय विश्लेषण" body={analysis} />
        )}
        {upay.length > 0 && (
          <div className="mb-6 rounded-xl bg-white/[0.03] border border-white/10 p-5" data-testid="ans-view-upay">
            <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-2">उपाय</div>
            <ul className="space-y-2 text-[14.5px] leading-relaxed">
              {upay.map((u, i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                  <span>
                    {typeof u === 'string'
                      ? u
                      : <><b className="text-amber-300">{u.type || u.category || 'उपाय'}:</b> {u.details || u.description}</>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {vyavharik && (
          <Section title="व्यावहारिक सलाह" body={vyavharik} />
        )}
        {closing && (
          <div className="mt-8 pt-6 border-t border-white/10 text-[13px] text-white/60 italic leading-relaxed" data-testid="ans-view-closing">
            {closing}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 text-center text-[12px] text-white/40">
          <div className="mb-1">— Vivek Mudgal, Vedic Astrologer</div>
          <div>WhatsApp / Call: <a href="tel:+919911194198" className="text-amber-400 underline">+91 9911194198</a></div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, body }) {
  return (
    <div className="mb-6 rounded-xl bg-white/[0.03] border border-white/10 p-5" data-testid={`ans-view-sec-${title.slice(0,10)}`}>
      <div className="text-[11px] uppercase tracking-wider text-amber-300 mb-2">{title}</div>
      <div className="text-[14.5px] leading-relaxed whitespace-pre-wrap">{body}</div>
    </div>
  );
}
